import { Plus, X, MapPin, Flag, Truck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/common";
import { TextField, SelectField, AreaField } from "@/components/fields";

const StopRow = ({ stop, index, update, remove }) => (
  <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/70 p-2" data-testid={`extra-stop-${index}`}>
    <select
      value={stop.type}
      onChange={(e) => update({ ...stop, type: e.target.value })}
      data-testid={`extra-stop-type-${index}`}
      className="h-10 rounded-md border border-slate-200 bg-white px-2 text-sm"
    >
      <option value="pickup">Extra pickup</option>
      <option value="delivery">Extra drop-off</option>
    </select>
    <Input
      value={stop.address}
      onChange={(e) => update({ ...stop, address: e.target.value })}
      placeholder="Full address"
      data-testid={`extra-stop-address-${index}`}
      className="h-10 flex-1 bg-white"
    />
    <Button type="button" variant="ghost" size="icon" onClick={remove} data-testid={`extra-stop-remove-${index}`} className="text-slate-400 hover:text-rose-600">
      <X className="h-4 w-4" />
    </Button>
  </div>
);

export const MoveSection = ({ form, set, errors, settings }) => {
  const stops = form.extra_stops;
  const updateStop = (i, s) => set("extra_stops", stops.map((x, j) => (j === i ? s : x)));
  return (
    <Panel title="Move information" id="section-move">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextField name="pickup_address" label={<span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-sky-600" />Pickup address</span>} required placeholder="12 Smith St, Parramatta NSW 2150" form={form} set={set} errors={errors} />
          <TextField name="delivery_address" label={<span className="inline-flex items-center gap-1.5"><Flag className="h-3.5 w-3.5 text-amber-600" />Delivery address</span>} required placeholder="45 Ocean Rd, Bondi NSW 2026" form={form} set={set} errors={errors} />
        </div>
        <div className="space-y-2">
          {stops.map((s, i) => (
            <StopRow key={i} index={i} stop={s} update={(v) => updateStop(i, v)} remove={() => set("extra_stops", stops.filter((_, j) => j !== i))} />
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => set("extra_stops", [...stops, { type: "pickup", address: "" }])} data-testid="btn-add-stop" className="border-dashed">
            <Plus className="h-4 w-4" /> Add pickup / delivery stop
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <SelectField name="truck_size" label="Truck capacity / size" options={settings.truck_sizes} form={form} set={set} errors={errors} />
          <TextField name="bedrooms" label="Bedrooms" inputMode="numeric" form={form} set={set} errors={errors} />
          {form.pricing_type !== "cubic" && <TextField name="cubic_metres" label="Estimated cubic metres" inputMode="decimal" suffix="m³" form={form} set={set} errors={errors} />}
          <TextField name="men" label="Removalists / men" inputMode="numeric" form={form} set={set} errors={errors} />
        </div>
      </div>
    </Panel>
  );
};

export const DetailsSection = ({ form, set, errors }) => (
  <Panel title="Job details & notes" id="section-details">
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-4" data-testid="driver-details-block">
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-sky-900"><Truck className="h-4 w-4" /> Job details – sent to the driver</div>
        <p className="mb-3 text-xs text-sky-800/80">Everything here goes on the crew brief and the printed run sheet.</p>
        <AreaField name="job_notes" label="Job details" rows={5} placeholder="Inventory, access details, parking, gate codes, what to call the customer about…" form={form} set={set} errors={errors} />
        <div className="mt-3"><AreaField name="special_requirements" label="Special requirements" rows={2} placeholder="e.g. piano, stairs at pickup, no lift, tight driveway" form={form} set={set} errors={errors} /></div>
      </div>
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4" data-testid="internal-notes-block">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-amber-900"><Lock className="h-4 w-4" /> Internal notes – office only</div>
          <p className="mb-3 text-xs text-amber-800/80">Never shown to the driver or the customer.</p>
          <AreaField name="internal_notes" label="Internal notes" rows={4} placeholder="Pricing history, difficult customer, follow-ups…" form={form} set={set} errors={errors} />
        </div>
        <AreaField name="customer_notes" label="Customer-facing notes" hint="Included in the customer confirmation message" rows={2} form={form} set={set} errors={errors} />
      </div>
    </div>
  </Panel>
);
